import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary/80 text-primary-foreground hover:bg-primary/90 shadow-elegant transition-all duration-200 hover:shadow-lg hover:scale-105 hover:-translate-y-0.5",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary/70 text-secondary-foreground hover:bg-secondary/80 shadow-warm transition-all duration-200 hover:shadow-lg hover:scale-105 hover:-translate-y-0.5",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        hero: "bg-gradient-primary/80 text-primary-foreground dark:text-white hover:opacity-90 shadow-elegant font-semibold transition-all duration-200 hover:shadow-lg hover:scale-105 hover:-translate-y-0.5 [&_svg]:dark:text-white",
        warm: "bg-wood-warm/80 text-primary-foreground hover:bg-wood-warm/90 shadow-warm transition-all duration-200 hover:shadow-lg hover:scale-105 hover:-translate-y-0.5",
        premium: "bg-gradient-warm/80 dark:bg-carne/90 text-primary-foreground dark:text-wood-dark hover:bg-gradient-warm dark:hover:bg-carne hover:shadow-elegant hover:border-wood-warm/40 dark:hover:border-carne/60 shadow-soft font-medium border border-wood-warm/20 dark:border-carne/40 transition-all duration-300 hover:shadow-xl hover:scale-110 hover:-translate-y-1 cursor-pointer",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        xl: "h-14 rounded-lg px-10 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
